import { Component, Input, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { EChartsOption } from 'echarts';
import { ChartService } from '../../services/chart.service';
import { ThemeService } from '../../services/theme.service';
import { ViewportService } from '../../services/viewport.service';

/**
 * Envoltura delgada de ngx-echarts.
 *
 * Las paginas arman su EChartsOption una sola vez en ngOnInit, pero el canvas
 * no hereda variables CSS: aqui se vuelve a teñir la opcion cada vez que cambia
 * el tema.
 *
 * Tambien traduce la altura: las paginas declaran la de escritorio y aqui se
 * ajusta al corte vigente.
 */
@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss',
  standalone: false,
})
export class ChartComponent {

  private readonly chartService = inject(ChartService);
  private readonly themeService = inject(ThemeService);
  private readonly viewport: ViewportService = inject(ViewportService);

  /** Opcion tal como la entrego la pagina, sin teñir. */
  private readonly source = signal<EChartsOption | undefined>(undefined);

  /** Altura de escritorio declarada por la pagina, sin traducir. */
  private readonly baseHeight: WritableSignal<number> = signal<number>(260);

  @Input({ required: true })
  public set options(value: EChartsOption) {
    this.source.set(value);
  }

  /**
   * La firma publica no cambia: la pagina sigue pasando su altura de
   * escritorio en pixeles y es el componente quien la adapta.
   */
  @Input()
  public set height(value: number) {
    this.baseHeight.set(value);
  }

  /** Opcion con el cromo del tema vigente; es la que consume ngx-echarts. */
  public readonly themedOptions = computed<EChartsOption>(() => {
    this.themeService.theme();

    const option = this.source();

    return option ? this.chartService.applyTheme(option) : {};
  });

  /**
   * Altura real del lienzo. Como es `computed`, girar el telefono o arrastrar
   * la ventana cruzando un corte la recalcula sin que la pagina intervenga.
   */
  public readonly scaledHeight: Signal<number> = computed<number>(() => {
    // `scaleHeight` ya lee el corte por dentro; leerlo aqui deja la dependencia
    // a la vista para que un refactor del servicio no la rompa en silencio.
    this.viewport.breakpoint();

    return this.viewport.scaleHeight(this.baseHeight());
  });
}
